import {
  DOCUMENT_REQUEST_CATEGORY,
  QUESTIONNAIRE_REQUEST_CATEGORY,
} from '../../../../common/constants/process-management';
import {
  CurrentInformationRequest,
  DocumentProgress,
  QuestionProgress,
} from '../CurrentRequestedDocumentsAndQuestions/types';

export const filterDocumentInformationRequests = (currentInformationRequests: CurrentInformationRequest[]) => {
  const documentRequests = currentInformationRequests.find(request => request.category === DOCUMENT_REQUEST_CATEGORY);
  return (documentRequests?.progress as DocumentProgress[]) || [];
};

export const filterQuestionnaireInformationRequests = (currentInformationRequests: CurrentInformationRequest[]) => {
  const questionRequests = currentInformationRequests.find(
    request => request.category === QUESTIONNAIRE_REQUEST_CATEGORY
  );
  return (questionRequests?.progress as QuestionProgress[]) || [];
};

/**
 * Filters currentInformationRequests to remove items based on company fields.
 * - Documents: removes items where both company_information_request_status and company_requested_document are null
 * - Questions: removes items where both company_question and company_information_request_status are null
 */
export const filterCurrentInformationRequests = (
  currentInformationRequests: CurrentInformationRequest[]
): CurrentInformationRequest[] => currentInformationRequests.map(request => {
  if (request.category === DOCUMENT_REQUEST_CATEGORY) {
    const filteredProgress = request.progress.filter((item): item is DocumentProgress => {
      const isDocumentProgress = 'files' in item && 'is_uploaded' in item && 'tags' in item;
      if (!isDocumentProgress) {
        return false;
      }

      const docItem = item as DocumentProgress & {
          company_information_request_status?: number | null;
          company_requested_document?: number | null;
        };

      return !(
        docItem.company_information_request_status === null &&
          docItem.company_requested_document === null
      );
    });

    return {
      ...request,
      progress: filteredProgress,
    };
  }

  if (request.category === QUESTIONNAIRE_REQUEST_CATEGORY) {
    const filteredProgress = request.progress.filter((item): item is QuestionProgress => {
      const isQuestionProgress =
          'children_questions' in item &&
          'question_type' in item &&
          'options' in item &&
          'answers' in item &&
          'parent_question' in item;

      if (!isQuestionProgress) {
        return false;
      }

      const questionItem = item as QuestionProgress & {
          company_question?: number | null;
          company_information_request_status?: number | null;
        };

      return !(questionItem.company_question === null && questionItem.company_information_request_status === null);
    });

    return {
      ...request,
      progress: filteredProgress,
    };
  }

  return request;
});

export const getDocumentProgressValue = (progress: DocumentProgress[]) => {
  const completed = progress?.filter(item => item.files.length > 0)?.length || 0;
  const total = progress?.length || 0;
  const pending = total - completed;
  const display = `${completed}/${total}`;
  const value = (completed / total) * 100;
  return { display, value, completed, total, pending };
};

export const getQuestionnaireProgressValue = (progress: QuestionProgress[]) => {
  const completed = progress?.filter(item => item.answers?.length > 0)?.length || 0;
  const total = progress?.length || 0;
  const pending = total - completed;
  const display = `${completed}/${total}`;
  const value = (completed / total) * 100;
  return { display, value, completed, total, pending };
};
